import { getPaypalScriptOptions } from '@/utils/paymentParams';
import {
  PayPalScriptProvider,
  BraintreePayPalButtons,
  usePayPalScriptReducer,
  type OnApproveBraintreeActions,
  type CreateOrderBraintreeActions,
} from '@paypal/react-paypal-js';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { buildPaypalPaymentParams } from '@/utils/paypal';
import type { BraintreePayPalCheckoutTokenizationOptions } from '@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout';
import { PaymentMethodEnum, ProviderEnum, Templates } from '@/types/common';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useTranslate } from '@/hooks/useTranslate';
import { LoaderPayButton } from '../LoaderPayButton/LoaderPayButton';
import type { OnClickActions } from '@paypal/paypal-js';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { ErrorBoundary } from 'react-error-boundary';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';

interface PaypalButtonUIProps {
  isLoading: boolean;
  onReady: () => void;
  onError: (error: unknown) => void;
  createOrder: (_: unknown, actions: CreateOrderBraintreeActions) => Promise<string>;
  onApprove: (
    paymentData: BraintreePayPalCheckoutTokenizationOptions,
    actions: OnApproveBraintreeActions
  ) => Promise<void>;
  onScriptReject: () => void;
}

const PaypalButtonUI = ({
  isLoading,
  onReady,
  onError,
  createOrder,
  onApprove,
  onScriptReject,
}: PaypalButtonUIProps) => {
  const { t } = useTranslate();
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    template,
    customer,
    checkoutConfiguration,
    payment: { isFormValid },
  } = context;
  const [{ isRejected }] = usePayPalScriptReducer();

  const isOnePayment = template.type === Templates.OnePayment;

  const handleClick = (_: Record<string, unknown>, actions: OnClickActions) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, PaymentMethodEnum.PAYPAL);
      return actions.resolve();
    }
    const errorMessageKey = getZipCodeErrorMessageKey(customer);
    setError(t(errorMessageKey));
    return actions.reject();
  };

  useEffect(() => {
    if (isRejected) {
      setError(t('paymentMethodNotAvailable'));
      onScriptReject();
    }
  }, [isRejected]);

  return (
    <div className="flex flex-col w-full min-h-11">
      <div className={`transition-opacity duration-500 ${!isLoading && 'hidden'}`}>
        <LoaderPayButton type={PaymentMethodEnum.PAYPAL} />
      </div>
      <div className={`transition-opacity duration-500 ${isLoading && 'hidden'}`}>
        {!isOnePayment && checkoutConfiguration?.shouldShowPayPalAsApm && (
          <div className="flex flex-col gap-2 pb-4 mt-2">
            <div className="text-base text-card-foreground font-bold">{t('paymentViaPaypal')}</div>
            <div className="text-sm text-muted-foreground">{t('paypalRedirectMessage')}</div>
          </div>
        )}
        <BraintreePayPalButtons
          createOrder={createOrder}
          className="w-full h-full !z-10"
          onApprove={onApprove}
          onInit={onReady}
          onError={onError}
          onClick={handleClick}
        />
      </div>
    </div>
  );
};

interface PaypalFormProps {
  onReady?: (isAvailable: boolean) => void;
}

export const PaypalForm = ({ onReady }: PaypalFormProps) => {
  const {
    paymentKeys,
    pricing,
    paymentMethodsSettings,
    actions: { runPayment, setError: setGlobalError },
    template,
  } = useContext(CheckoutContext);
  const paymentMethodSettings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.PAYPAL);
  const [isLoading, setIsLoading] = useState(true);
  const isOnePayment = template.type === Templates.OnePayment;
  const { t } = useTranslate();

  const handleReady = () => {
    setTimeout(() => setIsLoading(false), 500);
    onReady?.(true);
  };

  const handleScriptReject = useCallback(() => {
    onReady?.(false);
  }, [onReady]);

  const handleError = (error: unknown) => {
    console.error('PayPal error:', error);
    setIsLoading(false);

    const errorMessage = error instanceof Error ? error.message : t('somethingWentWrong');

    if (
      errorMessage.includes('Detected popup close') ||
      errorMessage.includes('Window is closed, can not determine type')
    ) {
      return;
    }

    setGlobalError(errorMessage);
  };

  const handleFatalError = (error: unknown) => {
    onReady?.(false);
    handleError(error);
  };

  const createOrder = async (_: unknown, actions: CreateOrderBraintreeActions) => {
    return actions.braintree.createPayment(buildPaypalPaymentParams(pricing));
  };

  const handlePaymentApprove = async (
    paymentData: BraintreePayPalCheckoutTokenizationOptions,
    actions: OnApproveBraintreeActions
  ) => {
    const tokenizationData = await actions.braintree.tokenizePayment(paymentData);
    try {
      await runPayment({ tokenizationData }, PaymentMethodEnum.PAYPAL);
    } catch (e) {
      throw e;
    }
  };

  const paypalKey =
    paymentMethodSettings?.provider === ProviderEnum.BRAINTREE ? paymentKeys.braintree : paymentKeys.paypal;
  const scriptOptions = getPaypalScriptOptions(paypalKey, pricing.currencyCode);

  if (!scriptOptions) {
    if (isOnePayment) {
      setGlobalError(t('paymentMethodNotAvailable'));
    }
    return null;
  }

  try {
    return (
      <div className="flex flex-col gap-4">
        <ErrorBoundary fallback={null} onError={handleFatalError}>
          <PayPalScriptProvider options={scriptOptions}>
            <PaypalButtonUI
              isLoading={isLoading}
              onReady={handleReady}
              onError={handleError}
              createOrder={createOrder}
              onApprove={handlePaymentApprove}
              onScriptReject={handleScriptReject}
            />
          </PayPalScriptProvider>
        </ErrorBoundary>
        <InputTaxComponent />
      </div>
    );
  } catch (error) {
    console.error('PayPal initialization error:', error);
    if (isOnePayment) {
      setGlobalError(t('paymentMethodNotAvailable'));
    }
    return null;
  }
};
